import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  LexicalNode,
} from 'lexical';
import { useEffect, useRef } from 'react';

import { $createSlotNode } from '../SlotNode';

type InitialValuePluginProps = {
  value: string;
};

export const InitialValuePlugin = ({
  value,
}: InitialValuePluginProps): null => {
  const [editor] = useLexicalComposerContext();
  const initializedValueRef = useRef<string | null>(null);

  useEffect(() => {
    const currentText = editor
      .getEditorState()
      .read(() => $getRoot().getTextContent());

    if (initializedValueRef.current === null || currentText !== value) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();

        if (!value) {
          return;
        }

        const slotPattern = /\{\{([\p{L}\p{N}_]+)\}\}/gu;
        let lastIndex = 0;
        const nodes: LexicalNode[] = [];
        const matches = Array.from(value.matchAll(slotPattern));

        for (const match of matches) {
          if (match.index !== undefined && match.index > lastIndex) {
            const textBefore = value.substring(lastIndex, match.index);
            nodes.push($createTextNode(textBefore));
          }

          const slotName = match[1];
          nodes.push($createSlotNode(slotName));

          lastIndex = (match.index ?? 0) + match[0].length;
        }

        if (lastIndex < value.length) {
          nodes.push($createTextNode(value.substring(lastIndex)));
        }

        if (nodes.length > 0) {
          const paragraph = $createParagraphNode();
          paragraph.append(...nodes);
          root.append(paragraph);
        }
      });
    }

    initializedValueRef.current = value;
  }, [editor, value]);

  return null;
};
